import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { goalKey, memberGoals } from "@/lib/goals";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { MemberDialog } from "@/components/household/MemberDialog";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Crown, Star } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function Household() {
  const { t } = useLang();
  const { refresh, user } = useAuth();
  const canManageProfiles = ["plus", "premium"].includes(user?.plan_tier);
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // {member|null}
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({ queryKey: ["household"], queryFn: async () => (await api.get("/household")).data });
  const members = data?.members || [];
  const raw = Object.fromEntries((data?.household?.members || []).map((m) => [m.member_id, m]));

  const done = async (msg) => {
    await Promise.all([qc.invalidateQueries({ queryKey: ["household"] }), qc.invalidateQueries({ queryKey: ["mealplan"] }), qc.invalidateQueries({ queryKey: ["grocery"] })]);
    toast.success(msg);
  };

  const save = async (form) => {
    setSaving(true);
    try {
      if (dialog?.member) await api.put(`/household/members/${dialog.member.member_id}`, form);
      else await api.post("/household/members", form);
      setDialog(null);
      await refresh();
      await done(t("member_saved"));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await api.delete(`/household/members/${id}`); await done(t("member_deleted")); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setPrimary = async (id) => {
    try { await api.post(`/household/primary/${id}`); await refresh(); await done(t("saved")); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const editData = (m) => {
    if (m.is_primary) {
      const p = m.profile || {};
      return { member_id: "primary", name: p.first_name || m.name, ...p, participates: m.participates, present_days: m.present_days };
    }
    return { ...raw[m.member_id], participates: m.participates, present_days: m.present_days };
  };

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4 chenia-fade-up">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-primary text-primary-foreground"><Users className="h-5 w-5" /></span>
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{t("household_title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base">{t("household_sub")}</p>
          </div>
        </div>
        {canManageProfiles ? <Button onClick={() => setDialog({ member: null })} className="rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="household-add-member">
          <Plus className="h-4 w-4 mr-1" /> {t("add_member")}
        </Button> : <div className="max-w-md rounded-2xl bg-secondary/70 p-4" data-testid="household-profiles-locked">
          <p className="text-sm">{t("household_profiles_locked")}</p>
          <div className="mt-3 flex flex-wrap gap-2"><Link to="/pricing?plan=plus"><Button size="sm" className="rounded-full">{t("demo_choose_plan", { plan: t("plan_plus") })}</Button></Link><Link to="/pricing?plan=premium"><Button size="sm" variant="outline" className="rounded-full">{t("demo_choose_plan", { plan: t("plan_premium") })}</Button></Link></div>
        </div>}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-4" data-testid="household-members">
        {members.map((m) => (
          <div key={m.member_id} className={`chenia-card p-5 ${!m.participates ? "opacity-60" : ""}`} data-testid={`member-card-${m.member_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-2xl bg-chenia-rosesoft text-chenia-terracottadark font-serif text-lg font-semibold">{(m.name || "?")[0]}</span>
                <div>
                  <p className="font-semibold flex items-center gap-1.5">{m.name} {m.is_primary && <Crown className="h-3.5 w-3.5 text-primary" />}</p>
                  <p className="text-xs text-muted-foreground">{t("years_short", { n: m.profile?.age })} · {memberGoals(m.profile).map((g) => t(goalKey(g))).join(", ")} · {t(m.profile?.diet || "omnivore")}</p>
                </div>
              </div>
              <p className="text-right shrink-0"><span className="font-serif text-xl font-semibold" data-testid={`member-kcal-${m.member_id}`}>{m.targets.calories}</span><br /><span className="text-[11px] text-muted-foreground">{t("kcal_day")}</span></p>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {(m.profile?.allergies || []).map((a) => <span key={a} className="text-[11px] rounded-full bg-destructive/10 text-destructive px-2 py-0.5">{t(`al_${a}`)}</span>)}
              {(m.profile?.excluded_foods || []).map((a) => <span key={a} className="text-[11px] rounded-full bg-secondary px-2 py-0.5">✕ {a}</span>)}
            </div>

            <div className="mt-3 flex gap-1" data-testid={`member-days-${m.member_id}`}>
              {DAYS.map((d) => (
                <span key={d} className={`h-6 w-6 grid place-items-center rounded-full text-[10px] font-semibold ${m.present_days.includes(d) ? "bg-chenia-sage/40" : "bg-secondary text-muted-foreground/50"}`}>{t(d)[0]}</span>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-2 border-t border-border pt-3">
              {(m.is_primary || canManageProfiles) && <Button variant="outline" size="sm" onClick={() => setDialog({ member: editData(m) })} className="rounded-full" data-testid={`member-edit-${m.member_id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> {t("edit_member")}</Button>}
              {!m.is_primary && (
                <>
                  {canManageProfiles && <Button variant="ghost" size="sm" onClick={() => setPrimary(m.member_id)} className="rounded-full" data-testid={`member-primary-${m.member_id}`}><Star className="h-3.5 w-3.5 mr-1" /> {t("set_primary")}</Button>}
                  <button onClick={() => remove(m.member_id)} className="ml-auto grid place-items-center h-8 w-8 rounded-full text-destructive hover:bg-destructive/10" data-testid={`member-delete-${m.member_id}`}><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          </div>
        ))}
        {members.length <= 1 && canManageProfiles && (
          <button onClick={() => setDialog({ member: null })} className="chenia-card p-6 border-dashed text-left text-muted-foreground hover:bg-secondary/40 transition-colors" data-testid="household-empty-add">
            <Plus className="h-5 w-5 mb-2 text-primary" />{t("no_members")}
          </button>
        )}
      </div>

      <p className="mt-6 text-xs text-muted-foreground leading-relaxed" data-testid="household-note">{t("household_note")}</p>

      <MemberDialog open={!!dialog} onClose={() => setDialog(null)} member={dialog?.member} onSave={save} saving={saving} />
    </AppShell>
  );
}
